<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { whatsappLink } from '@/lib/whatsapp'
import { trackConversion, trackFabricView, trackQuoteCtaClick } from '@/lib/analytics'
import { requestQuoteFor } from '@/lib/quote_intent'
import ProductCarousel from '@/components/ProductCarousel.vue'
import FabricSwatch from '@/components/FabricSwatch.vue'
import WhatsappIcon from '@/components/WhatsappIcon.vue'

const props = defineProps({
  // null cuando no hay ficha abierta.
  fabric: { type: Object, default: null },
  phone: { type: String, required: true }
})

const emit = defineEmits([ 'close' ])

const dialog = ref(null)
const details = ref(null)
const subject = computed(() => {
  if (!props.fabric) return ''

  return `una cortina en tela ${props.fabric.name.toLowerCase()}`
})

const whatsappHref = computed(() => whatsappLink(props.phone, subject.value))
const photos = computed(() => props.fabric?.photos ?? [])
const description = computed(() => props.fabric?.description || props.fabric?.note)
const colours = computed(() => (photos.value.length > 1 ? photos.value.length : 0))

watch(() => props.fabric, async (fabric) => {
  if (fabric) {
    dialog.value?.showModal()

    await nextTick()
    if (details.value) details.value.scrollTop = 0
    document.body.style.overflow = 'hidden'
    trackFabricView(fabric.name)
  } else {
    dialog.value?.close()
    document.body.style.overflow = ''
  }
})

function onClose () {
  document.body.style.overflow = ''
  emit('close')
}

function onBackdropClick (event) {
  if (event.target === dialog.value) dialog.value.close()
}

function goToForm () {
  trackQuoteCtaClick('fabric-dialog')
  dialog.value?.close()
  requestQuoteFor(null)
}
</script>

<template>
  <dialog
    ref="dialog"
    class="sheet-dialog"
    :aria-label="fabric ? `Ficha de la tela ${fabric.name}` : undefined"
    @close="onClose"
    @click="onBackdropClick"
  >
    <div
      v-if="fabric"
      class="flex max-h-[92dvh] flex-col lg:grid lg:h-[80dvh] lg:max-h-none lg:grid-cols-[1.05fr_1fr]"
    >
      <button
        type="button"
        class="absolute end-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-night/60 text-linen backdrop-blur transition hover:bg-night"
        aria-label="Cerrar"
        @click="dialog.close()"
      >
        <svg viewBox="0 0 24 24" fill="none" class="h-4.5 w-4.5" aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <div class="h-[28dvh] shrink-0 p-3 sm:h-[38dvh] sm:p-4 lg:h-full lg:min-h-0 lg:p-5">
        <ProductCarousel
          v-if="photos.length"
          :photos="photos"
          :label="`la tela ${fabric.name}`"
        />

        <div v-else class="flex h-full items-center justify-center">
          <div class="aspect-square h-full overflow-hidden rounded-md ring-1 ring-inset ring-linen/10">
            <FabricSwatch :fabric="fabric" loading="eager" />
          </div>
        </div>
      </div>

      <div
        ref="details"
        class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pb-5 pt-1 sm:gap-5 sm:px-7 sm:pb-7 lg:py-7 lg:pe-7 lg:ps-2"
      >
        <div class="shrink-0 lg:pe-12">
          <h2 class="display text-balance text-[clamp(1.6rem,6vw,2.4rem)] text-linen">
            {{ fabric.name }}
          </h2>
          <p v-if="description" class="mt-4 leading-relaxed text-linen/75">
            {{ description }}
          </p>
        </div>

        <p v-if="colours" class="shrink-0 border-y border-linen/10 py-3 leading-relaxed text-linen/80 sm:py-4">
          {{ colours }} colores disponibles
        </p>

        <div class="mt-auto shrink-0 pt-1">
          <div class="mt-2 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-full bg-glow px-3 py-2.5 text-sm font-semibold text-night transition-colors duration-300 hover:bg-[#f4a62a]"
              @click="goToForm"
            >
              <svg viewBox="0 0 24 24" fill="none" class="h-[1.05em] w-[1.05em] shrink-0" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="2" />
                <path d="m4 7.5 8 5 8-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Cotizar
            </button>

            <a
              :href="whatsappHref"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center justify-center gap-2 rounded-full border border-linen/25 px-3 py-2.5 text-sm font-semibold text-linen/90 transition-colors duration-300 hover:bg-linen/10"
              @click="trackConversion('fabric-dialog', subject)"
            >
              <WhatsappIcon class="h-[1.05em] w-[1.05em] shrink-0" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  </dialog>
</template>
